import Loading from "@/components/Loading";
import { useUserContext } from "@/hooks/useUserContext";
import {
  ArrowUpRightIcon,
  BadgePercentIcon,
  BrainCogIcon,
  ChartNoAxesCombinedIcon,
  FlameIcon,
  HandCoinsIcon,
  LockIcon,
  MapPin,
  MapPinIcon,
  PictureInPicture2Icon,
  PiggyBankIcon,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useAuthContext } from "@/hooks/useAuthContext";
import Subscriptions from "../Subscriptions/Subscriptions";
import { useCollection } from "@/hooks/useCollection";
import { useDocument } from "@/hooks/useDocument";

export default function Dashboard({
  sidebarExpanded,
  setSidebarExpanded,
  isWatching,
  setIsWatching,
  activeRoute,
  setActiveRoute,
}) {
  const { user } = useAuthContext();
  const { userDoc } = useUserContext();
  const { documents: progressDocs } = useCollection("progress", [
    "userId",
    "==",
    user.uid,
  ]);
  const { document: maiaDoc } = useDocument("maia", "rentability");
  const isWideScreen = useMediaQuery("(min-width: 1920px)");
  const navigate = useNavigate();

  const options = [
    {
      name: "Primeiros passos",
      icon: <MapPinIcon className="h-6 w-6 sm:w-8 sm:h-8 shrink-0 text-icon" />,
      description:
        "Entenda o que é a Money Academy e qual é o passo-a-passo que você deve percorrer aqui dentro para mudar sua vida financeira para sempre.",
    },
  ];

  const options2 = [
    {
      name: "Fundamentos da Prosperidade",
      icon: (
        <HandCoinsIcon className="h-6 w-6 sm:w-8 sm:h-8 shrink-0 text-icon" />
      ),
      description:
        "Entenda os conceitos básicos que você precisa saber para começar a poupar, investir e a prosperar financeiramente.",
    },
    {
      name: "Margin Trading",
      icon: (
        <BadgePercentIcon className="h-6 w-6 sm:w-8 sm:h-8 shrink-0 text-icon" />
      ),
      description:
        "Aprofunde-se no conceito de Margin Trading e saiba como você pode alavancar seus investimentos para obter lucros ainda maiores.",
    },
    {
      name: "Finanças Pessoais",
      icon: (
        <PiggyBankIcon className="h-6 w-6 sm:w-8 sm:h-8 shrink-0 text-icon" />
      ),
      description:
        "Aprenda a organizar suas finanças pessoais e a criar um planejamento financeiro que te permita alcançar seus objetivos de vida.",
    },
  ];

  if (!userDoc) return <Loading />;

  const purchaseDate = userDoc.plan.purchase_date.toDate();
  // User should have 3 days after purchasing to link their account
  const threeDaysAfterPurchase = new Date(purchaseDate);
  threeDaysAfterPurchase.setDate(threeDaysAfterPurchase.getDate() + 3);
  let daysLeft = Math.ceil(
    (threeDaysAfterPurchase - new Date()) / (1000 * 60 * 60 * 24)
  );
  if (daysLeft < 0) daysLeft = 3;

  if (userDoc.plan.status !== "active") return <Subscriptions />;

  return (
    <div className="h-[200vh] py-2.5 px-0 sm:px-0 lg:w-1/2">
      {userDoc.accountNumber ? null : (
        <div
          className="text-[13px] sm:text-[15px] -mt-2.5 mb-5 bg-red-500/5 border border-red-500 rounded-md p-3 sm:p-6 cursor-pointer"
          onClick={() => {
            if (userDoc.meaningfulActions?.clickedAffiliateLink?.status) {
              setActiveRoute(1);
              navigate("/maia");
            } else {
              setActiveRoute(0);
              navigate("/assistir");
            }
          }}
        >
          <p className="text-red-500">
            Você só tem mais <b>{daysLeft} dias</b> para ativar a MA.IA™
            gratuitamente.
          </p>
          {userDoc.meaningfulActions?.clickedAffiliateLink?.status ? (
            <p className="mt-1.5 text-red-500">
              Já ativou?{" "}
              <span className="font-semibold underline">
                Clique aqui para vincular sua conta.
              </span>
            </p>
          ) : (
            <p className="mt-1.5 text-red-500">
              Complete a unidade <b>Primeiros Passos</b> para não perder seu
              bônus.
            </p>
          )}
        </div>
      )}
      <div className="flex gap-2.5 mb-1">
        <ChartNoAxesCombinedIcon className="text-icon" />
        <h1 className="text-xl font-medium text-black/75">
          Rentabilidade MA.IA™
        </h1>
      </div>
      <p className="text-xs text-muted-foreground mb-2.5">
        Confira o lucro da MA.IA™ nos últimos dias.
      </p>
      <div className="mb-5 flex items-center sm:gap-5 border p-3 sm:p-6 bg-white rounded-md">
        <div className="flex flex-wrap justify-between gap-y-1.5 sm:gap-y-3 w-full">
          {maiaDoc ? (
            Object.keys(maiaDoc)
              .sort()
              .map((key, i) => {
                if (key !== "id") {
                  return (
                    <p
                      key={key}
                      className={`${
                        key === "hoje" || key === "last07Days"
                          ? "w-1/2"
                          : "w-1/4"
                      }  text-muted-foreground text-[13px] sm:text-[15px] ${
                        maiaDoc[key].display
                      }`}
                    >
                      {maiaDoc[key].label}:{" "}
                      <span className="text-green-500 font-medium">
                        +{maiaDoc[key].value}
                      </span>
                    </p>
                  );
                }
              })
          ) : (
            <p>Carregando...</p>
          )}
        </div>
      </div>
      <div className="flex gap-3 items-center">
        <MapPinIcon className="text-icon" />
        <h1 className="text-xl font-medium text-black/75">Comece por aqui</h1>
      </div>
      <div className="mt-3 flex gap-6">
        {options.map((option) => (
          <div
            key={option.name}
            className="flex items-center sm:gap-5 border p-3 sm:p-6 bg-white //xl:w-[45%] rounded-md cursor-pointer"
            onClick={() => {
              if (!isWideScreen) {
                setSidebarExpanded(false);
              }
              setIsWatching(false);
              navigate("/assistir");
            }}
          >
            <div className="flex flex-col gap-1.5">
              <p className="text-md font-medium text-icon">{option.name}</p>
              <p className="text-black/75 text-[13px] sm:text-sm">
                {option.description}
              </p>
            </div>
            {option.icon}
          </div>
        ))}
      </div>
      {user.uid !== "bUN929XbjjQtHKoFEiP4u8U0APk1" && (
        <>
          <div className="mt-5 flex gap-3 items-center">
            <BrainCogIcon className="text-icon" />
            <h1 className="text-xl font-medium text-black/75">MA.IA™ 🔥 </h1>
          </div>
          <div className="mt-1">
            <p className="text-xs text-muted-foreground">
              Conclua o módulo "Comece por aqui" para desbloquear a nossa
              Inteligência Artificial.
            </p>
          </div>
          <div className="mt-2.5">
            <div
              className={`${
                progressDocs?.length === 10 ? "" : "opacity-50 grayscale"
              } brightness-25 relative flex items-center flex-nowrap justify-between gap-5 border p-3 sm:p-6 bg-white //xl:w-[45%] rounded-md cursor-pointer`}
              role="button"
              onClick={() => navigate("/maia")}
            >
              <div className="flex flex-col gap-1.5">
                <p className="text-md font-medium text-icon">
                  Renda em dólar com Inteligência Artificial
                </p>
                <p className="text-black/75 text-sm">
                  Acesse a nossa IA e descubra as melhores oportunidades de
                  investimento.
                </p>
              </div>
              {progressDocs?.length === 10 ? (
                <BrainCogIcon className="h-6 w-6 sm:w-8 sm:h-8 shrink-0 text-icon" />
              ) : (
                <LockIcon className="h-6 w-6 sm:w-8 sm:h-8 shrink-0" />
              )}
            </div>
          </div>
        </>
      )}
      <div className="mt-5 flex gap-3 items-center">
        <PictureInPicture2Icon className="text-icon" />
        <h1 className="text-xl font-medium text-black/75">Conteúdo</h1>
      </div>
      <div className="mt-1">
        <p className="text-xs text-muted-foreground">
          Conclua o módulo "Comece por aqui" para desbloquear o restante do
          conteúdo.
        </p>
      </div>
      <div className="mt-2.5 flex flex-col lg:flex-row gap-2.5 sm:gap-6 flex-wrap">
        {options2.map((option) => (
          <div
            key={option.name}
            className={`${
              userDoc.startHere ? "" : "opacity-50 grayscale"
            } brightness-25 relative flex flex-nowrap items-center gap-2.5 sm:gap-5 border p-3 sm:p-6 bg-white //xl:w-[45%] rounded-md cursor-pointer`}
          >
            <div className="flex flex-col gap-1.5">
              <p className="text-md font-medium text-icon">{option.name}</p>
              <p className="text-black/75 text-sm">{option.description}</p>
            </div>
            {userDoc.startHere ? (
              option.icon
            ) : (
              <LockIcon className="h-6 w-6 sm:w-8 sm:h-8 shrink-0" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
